import { BAN_MAX_MINUTES, FEEDBACK_PAUSE_MAX_MINUTES, FREEZE_MAX_MINUTES, MAX_NAME_PART_BYTES, moderationText, type LiftableAction, type ModerationAction } from "odyssey-shared";
import type { GmCommand } from "./words.ts";

/**
 * A GM's chat commands as requests (ADR-056's amendment of 2026-10-04, item 10), read on the page before anything
 * is sent: `/kick Family reason`, `/freeze Family [10m] reason`, `/warn Family reason`, `/flag Family note` (the
 * staff's note, whose action is `alert`; `/alert` is a second word for it, ADR-056's amendment of 2026-10-06),
 * `/ban Family 7d reason` (`30m`, `12h`, `7d` or `perm`), `/lift Family reason`, which ends the family's ban and its
 * freeze, `/unban Family reason` and `/unfreeze Family reason`, each a lift that names the one kind it ends (the
 * amendment of 2026-10-06, decision 11), `/actions` and `/gm`; and, on an
 * account's access to feedback (ADR-081's amendment of 2026-10-05), `/feedbackpause Family 24h reason` (a length as a
 * ban's, 1 minute to 30 days, never `perm`), `/feedbackresume Family reason`, `/feedbackoff Family reason` and
 * `/feedbackon Family reason`. The page
 * holds the family to a name part and the reason to the rule the gateway applies, so it refuses what the gateway
 * would, with the command's usage, without sending. The gateway decides everything else.
 */

/**
 * What a GM's command asks for: an action on a family, the list of standing actions, the Moderation window, or,
 * for a command out of its syntax, its usage. A lift from `/unban` or `/unfreeze` carries `ends`, the one kind it
 * ends; every other request has no such field.
 */
export type ModerationAsk =
  | { readonly kind: "act"; readonly action: ModerationAction; readonly familyName: string; readonly minutes: number | undefined; readonly reason: string; readonly ends?: LiftableAction }
  | { readonly kind: "list" }
  | { readonly kind: "window" }
  | { readonly kind: "usage"; readonly command: GmCommand };

/**
 * A family as a command names it: the characters every name admits, at most the wire's 32.
 */
const FAMILY = new RegExp(`^[A-Za-z0-9_-]{1,${MAX_NAME_PART_BYTES}}$`);

/**
 * A command's first word and what follows it as typed.
 */
const FIRST_WORD = /^(\S+)(?:\s+([\s\S]*))?$/;

/**
 * A ban's length: a whole number with `m` for minutes, `h` for hours or `d` for days; or `perm`.
 */
const BAN_LENGTH = /^(\d{1,5})([mhd])$/i;

/**
 * A freeze's length: whole minutes with their `m`, so a reason that starts with a number is still a reason.
 */
const FREEZE_LENGTH = /^(\d{1,3})m$/i;

/**
 * The minutes each unit of a ban's length holds.
 */
const UNIT_MINUTES: Readonly<Record<string, number>> = { m: 1, h: 60, d: 24 * 60 };

/**
 * The GM commands by their words, in lower case: each action's own word, `flag` for the staff's note beside its
 * stored name `alert`, `unban` and `unfreeze`, `actions` and `gm`.
 */
const BY_WORD: ReadonlyMap<string, GmCommand> = new Map<string, GmCommand>([
  ["kick", "kick"],
  ["freeze", "freeze"],
  ["warn", "warn"],
  ["flag", "alert"],
  ["alert", "alert"],
  ["ban", "ban"],
  ["lift", "lift"],
  ["unban", "unban"],
  ["unfreeze", "unfreeze"],
  ["feedbackpause", "feedbackpause"],
  ["feedbackresume", "feedbackresume"],
  ["feedbackoff", "feedbackoff"],
  ["feedbackon", "feedbackon"],
  ["actions", "actions"],
  ["gm", "gm"],
]);

/**
 * Every GM command, once each: the set a usage line exists for.
 */
export const GM_COMMANDS: readonly GmCommand[] = [...new Set(BY_WORD.values())];

/**
 * The GM command a word names, whole and without regard to case. The chat command map names none of these words.
 *
 * @param word The word after `/`.
 * @returns The command, or null when the word names none.
 */
export function gmCommandNamed(word: string): GmCommand | null {
  return BY_WORD.get(word.toLowerCase()) ?? null;
}

/**
 * A ban's minutes as typed.
 *
 * @param word The length as typed.
 * @returns Its minutes, 0 for `perm`, or null when it is out of the syntax or the bound.
 */
function banMinutes(word: string): number | null {
  if (word.toLowerCase() === "perm") {
    return 0;
  }
  const parts = BAN_LENGTH.exec(word);
  if (parts === null) {
    return null;
  }
  const minutes = Number(parts[1]) * (UNIT_MINUTES[(parts[2] ?? "").toLowerCase()] ?? 1);
  return minutes >= 1 && minutes <= BAN_MAX_MINUTES ? minutes : null;
}

/**
 * A feedback pause's minutes as typed: a ban's lengths, with no `perm`.
 *
 * @param word The length as typed.
 * @returns Its minutes, or null when it is out of the syntax or the bound.
 */
function pauseMinutes(word: string): number | null {
  const parts = BAN_LENGTH.exec(word);
  if (parts === null) {
    return null;
  }
  const minutes = Number(parts[1]) * (UNIT_MINUTES[(parts[2] ?? "").toLowerCase()] ?? 1);
  return minutes >= 1 && minutes <= FEEDBACK_PAUSE_MAX_MINUTES ? minutes : null;
}

/**
 * The minutes the Moderation window's length field asks for, read as the commands read a length: a ban's `30m`,
 * `12h`, `7d` or `perm`; a feedback pause's `30m`, `12h` or `7d`; a freeze's minutes with or without their `m`, or
 * nothing for the default; and nothing for every other action.
 *
 * @param action The action the form records.
 * @param word The field's text.
 * @returns The minutes, 0 for a permanent ban and undefined for none; or null when the text is not a length the
 *   action takes.
 * @example
 * lengthMinutes("ban", "7d");
 * // 10080
 */
export function lengthMinutes(action: ModerationAction, word: string): number | undefined | null {
  const typed = word.trim();
  if (action === "ban") {
    return banMinutes(typed);
  }
  if (action === "feedbackpause") {
    return pauseMinutes(typed);
  }
  if (typed === "") {
    return undefined;
  }
  if (action !== "freeze" || !/^\d{1,3}m?$/i.test(typed)) {
    return null;
  }
  const minutes = Number.parseInt(typed, 10);
  return minutes >= 1 && minutes <= FREEZE_MAX_MINUTES ? minutes : null;
}

/**
 * The kind each narrow word for a lift ends.
 */
const ENDS: Readonly<Record<"unban" | "unfreeze", LiftableAction>> = { unban: "ban", unfreeze: "freeze" };

/**
 * Reads what follows a GM command's word.
 *
 * @param command The command.
 * @param rest What follows its word, as typed.
 * @returns The request, or the command's usage when it is out of its syntax.
 * @example
 * readModerationCommand("ban", "Ashgrove 7d spamming Trade");
 * // { kind: "act", action: "ban", familyName: "Ashgrove", minutes: 10080, reason: "spamming Trade" }
 */
export function readModerationCommand(command: GmCommand, rest: string): ModerationAsk {
  const usage: ModerationAsk = { kind: "usage", command };
  if (command === "actions" || command === "gm") {
    return rest.trim() !== "" ? usage : command === "actions" ? { kind: "list" } : { kind: "window" };
  }
  const named = FIRST_WORD.exec(rest.trim());
  const familyName = named?.[1];
  let after = named?.[2] ?? "";
  if (familyName === undefined || !FAMILY.test(familyName)) {
    return usage;
  }
  let minutes: number | undefined;
  if (command === "ban" || command === "freeze" || command === "feedbackpause") {
    const length = FIRST_WORD.exec(after);
    const word = length?.[1] ?? "";
    if (command === "ban" || command === "feedbackpause") {
      const asked = command === "ban" ? banMinutes(word) : pauseMinutes(word);
      if (asked === null) {
        return usage;
      }
      minutes = asked;
      after = length?.[2] ?? "";
    } else if (FREEZE_LENGTH.test(word)) {
      minutes = Number(word.slice(0, -1));
      if (minutes < 1 || minutes > FREEZE_MAX_MINUTES) {
        return usage;
      }
      after = length?.[2] ?? "";
    }
  }
  const reason = moderationText(after);
  if (reason === null) {
    return usage;
  }
  return command === "unban" || command === "unfreeze" ? { kind: "act", action: "lift", familyName, minutes, reason, ends: ENDS[command] } : { kind: "act", action: command, familyName, minutes, reason };
}
